/**
 * @typedef {import('./EditablePropertyDetails').default} EditablePropertyDetails
 */

import { mapRawPropertyDetails } from '../HomeownerService/mapPropertyDetails';

const PRIMARY_HOME_CATEGORY = 'primary_home';

/**
 * Dot notated mapping of keys to set from returned property
 * @type {Map<string, string>}
 */
const fieldMap = new Map([
	['acreage', 'lot_size_acres'],
	['amountToLoan', 'mortgage.mtg_loan_amount'],
	['amortizationYears', 'mortgage.mtg_amortization_years'],
	['basement', 'basement'],
	['bedrooms', 'number_of_bedrooms'],
	['buildYear', 'year_built'],
	['constructionType', 'type_construction'],
	['dueDate', 'mortgage.mtg_due_date'],
	['escrowInsuranceMonthly', 'mortgage.mtg_escrow_insurance_monthly'],
	['escrowInsuranceAmount', 'mortgage.mtg_escrow_insurance_amount'],
	['escrowInsuranceFrequency', 'mortgage.mtg_escrow_insurance_frequency'],
	['escrowTaxMonthly', 'mortgage.mtg_escrow_tax_monthly'],
	['escrowTaxAmount', 'mortgage.mtg_escrow_tax_amount'],
	['escrowTaxFrequency', 'mortgage.mtg_escrow_tax_frequency'],
	['fireplace', 'fireplace'],
	['floorCover', 'floor_cover'],
	['foundationType', 'foundation'],
	['garageCars', 'garage_cars'],
	['garageType', 'garage_type'],
	['heating', 'heating.heating'],
	['heatingFuelType', 'heating.heating_fuel_type'],
	['interiorWalls', 'interior_walls'],
	['lenderName', 'mortgage.mtg_lender_name'],
	['loanType', 'mortgage.mtg_loan_type'],
	['lotSizeSquareFootage', 'lot_size_square_feet'],
	['maximumInterestRate', 'mortgage.mtg_maximum_interest_rate'],
	['numberOfBaths', 'number_of_baths'],
	['numberOfPartialBaths', 'number_of_partial_baths'],
	['originalDateOfContract', 'mortgage.mtg_original_date_of_contract'],
	['otherRooms', 'other_rooms'],
	['plumbingFixtures', 'n_of_plumbing_fixtures'],
	['pool', 'pool'],
	['rate', 'mortgage.mtg_interest_rate'],
	['roofCover', 'roof_cover'],
	['roofType', 'roof_type'],
	['sewer', 'sewer'],
	['stories', 'no_of_stories'],
	['style', 'style'],
	['totalNumberOfRooms', 'total_number_of_rooms'],
	['water', 'water'],
]);

/**
 * @param {{ property: any; }} data
 * @returns {EditablePropertyDetails}
 */
// eslint-disable-next-line complexity
export default function mapEditablePropertyDetails(data) {
	const { property } = data;
	const details = property?.details;
	const mappedDetails = mapRawPropertyDetails(details, fieldMap);

	mappedDetails.amortizationYears ??= details?.amortization_years ?? null;
	mappedDetails.amountToLoan ??= details?.amount_to_loan ?? null;
	mappedDetails.dueDate ??= details?.due_date ?? null;
	mappedDetails.escrowInsuranceAmount ??=
		details?.escrow_insurance_amount ?? null;
	mappedDetails.escrowInsuranceFrequency ??=
		details?.escrow_insurance_frequency ?? null;
	mappedDetails.escrowInsuranceMonthly ??=
		details?.escrow_insurance_monthly ?? null;
	mappedDetails.escrowTaxAmount ??= details?.escrow_tax_amount ?? null;
	mappedDetails.escrowTaxFrequency ??= details?.escrow_tax_frequency ?? null;
	mappedDetails.escrowTaxMonthly ??= details?.escrow_tax_monthly ?? null;
	mappedDetails.lenderName ??= details?.lender_name ?? null;
	mappedDetails.loanType ??= details?.loan_type ?? null;
	mappedDetails.maximumInterestRate ??=
		details?.maximum_interest_rate ?? null;
	mappedDetails.originalDateOfContract ??=
		details?.original_date_of_contract ?? null;
	mappedDetails.rate ??= details?.rate ?? null;

	return {
		address: {
			city: property.address.city,
			country: property.address.country,
			postalCode: property.address.postal_code,
			state: property.address.state,
			streetAddress1: property.address.street_address_1,
			streetAddress2: property.address.street_address_2,
		},
		category:
			property.category ??
			(property.is_primary || property.isPrimary
				? PRIMARY_HOME_CATEGORY
				: null),
		// @ts-ignore properties are defined in `fieldMap`
		details: mappedDetails,
		image: property.image
			? {
					id: property.image.id,
					path: property.image.path,
			  }
			: null,
		title: property.title ?? '',
	};
}
